/**
 * PGN Dialog Component
 * UI for exporting and importing games in PGN format
 */

import React, { useState } from 'react';
import './PGNDialog.css';

interface PGNDialogProps {
  mode: 'export' | 'import';
  pgnText?: string; // For export mode
  onClose: () => void;
  onImport?: (pgnText: string) => void; // For import mode
}

/**
 * PGN Dialog component for save/load functionality
 */
export const PGNDialog: React.FC<PGNDialogProps> = ({ mode, pgnText = '', onClose, onImport }) => {
  const [inputText, setInputText] = useState('');
  const [copied, setCopied] = useState(false);

  /**
   * Handle copy to clipboard
   */
  const handleCopy = async () => {
    try {
      await navigator.clipboard.writeText(pgnText);
      setCopied(true);
      setTimeout(() => setCopied(false), 2000);
    } catch (error) {
      console.error('Failed to copy:', error);
    }
  };

  /**
   * Handle download as file
   */
  const handleDownload = () => {
    const blob = new Blob([pgnText], { type: 'text/plain' });
    const url = URL.createObjectURL(blob);
    const a = document.createElement('a');
    a.href = url;
    a.download = `chess-game-${new Date().toISOString().split('T')[0]}.pgn`;
    document.body.appendChild(a);
    a.click();
    document.body.removeChild(a);
    URL.revokeObjectURL(url);
  };

  /**
   * Handle import
   */
  const handleImport = () => {
    if (onImport && inputText.trim()) {
      onImport(inputText.trim());
    }
  };

  /**
   * Handle file upload
   */
  const handleFileUpload = (event: React.ChangeEvent<HTMLInputElement>) => {
    const file = event.target.files?.[0];
    if (file) {
      const reader = new FileReader();
      reader.onload = (e) => {
        const text = e.target?.result as string;
        setInputText(text);
      };
      reader.readAsText(file);
    }
  };

  return (
    <div className="pgn-dialog-overlay" onClick={onClose}>
      <div className="pgn-dialog" onClick={(e) => e.stopPropagation()}>
        <div className="pgn-dialog-header">
          <h2>{mode === 'export' ? 'Export Game (PGN)' : 'Import Game (PGN)'}</h2>
          <button className="pgn-dialog-close" onClick={onClose} aria-label="Close">
            ✕
          </button>
        </div>

        <div className="pgn-dialog-content">
          {mode === 'export' ? (
            <>
              <p className="pgn-dialog-description">
                Copy the PGN text below or download it as a file:
              </p>
              <textarea
                className="pgn-textarea"
                value={pgnText}
                readOnly
                rows={15}
              />
              <div className="pgn-dialog-actions">
                <button className="pgn-button pgn-button-primary" onClick={handleCopy}>
                  {copied ? '✓ Copied!' : 'Copy to Clipboard'}
                </button>
                <button className="pgn-button pgn-button-secondary" onClick={handleDownload}>
                  Download as File
                </button>
                <button className="pgn-button pgn-button-tertiary" onClick={onClose}>
                  Close
                </button>
              </div>
            </>
          ) : (
            <>
              <p className="pgn-dialog-description">
                Paste PGN text below or upload a PGN file:
              </p>
              <textarea
                className="pgn-textarea"
                value={inputText}
                onChange={(e) => setInputText(e.target.value)}
                placeholder="Paste PGN text here..."
                rows={15}
              />
              <div className="pgn-dialog-file-upload">
                <label htmlFor="pgn-file-input" className="pgn-file-label">
                  Or upload PGN file:
                </label>
                <input
                  id="pgn-file-input"
                  type="file"
                  accept=".pgn"
                  onChange={handleFileUpload}
                  className="pgn-file-input"
                />
              </div>
              <div className="pgn-dialog-actions">
                <button 
                  className="pgn-button pgn-button-primary" 
                  onClick={handleImport}
                  disabled={!inputText.trim()}
                >
                  Load Game
                </button>
                <button className="pgn-button pgn-button-tertiary" onClick={onClose}>
                  Cancel
                </button>
              </div>
            </>
          )}
        </div>
      </div>
    </div>
  );
};
